import { Typography } from "@mui/material";
import { useAppSelector } from "app/hooks";
import { SuccessfulImports as AchievementsSuccessfulImports } from "components/Layout/Achievement/AchievementImportList/SuccessfullImports";
import { SuccessfulImports as AutomationSuccessfulImports } from "components/Layout/Campaign/Automations/AutomationsImportList/SuccessfulImports";
import { SuccessfulImports as CampaignsSuccessfulImports } from "components/Layout/Campaign/Direct/CampaignImportList/SuccessfulImports";
import { SuccessfulImports as ReferralsSuccessfullImports } from "components/Layout/Campaign/Referrals/ReferralsImportList/SuccessfullImports";
import { SuccessfulImports as CustomSchemaSuccesfulImports } from "components/Layout/CustomEvent/SchemaImportList/SuccessfullImports";
import { SuccessfulImports as RewardsSuccessfulImports } from "components/Layout/Reward/RewardImportList/SuccessfullImports";
import { SuccessfulImports as WalletSuccessfullImports } from "components/Layout/WalletType/WalletTypeImportList/SuccessfulImports";
import { useMessage } from "hooks/intl";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { ImportedAchievement } from "types/achievements";
import { CampaignBEData } from "types/campaigns";
import { ImportedCustomEventSchema } from "types/customEvents";
import { RewardFieldsBE } from "types/rewards";
import { BEWalletTypeFormat } from "types/wallets";
import { ImportTableWrapper } from "./ImportTableWrapper";
import { useStyles } from "./styles";
import { Tab } from "./Tabs/Tabs";

interface Props {
  successfullImport: {
    [Tab.WALLET]?: BEWalletTypeFormat[];
    [Tab.CUSTOM_EVENT]?: ImportedCustomEventSchema[];
    [Tab.REWARDS]?: RewardFieldsBE[];
    [Tab.ACHIEVEMENTS]?: ImportedAchievement[];
    [Tab.CAMPAIGNS]?: CampaignBEData[];
    [Tab.REFERRALS]?: CampaignBEData[];
    [Tab.AUTOMATIONS]?: CampaignBEData[];
  };
}

export const SuccessfulImports = ({ successfullImport }: Props) => {
  const { message } = useMessage();
  const classes = useStyles();
  const originStore = useAppSelector(selectCurrentStoreData).name;

  const areWallets =
    successfullImport[Tab.WALLET] && successfullImport[Tab.WALLET].length > 0;
  const areCustomEvents =
    successfullImport[Tab.CUSTOM_EVENT] &&
    successfullImport[Tab.CUSTOM_EVENT].length > 0;
  const areRewards =
    successfullImport[Tab.REWARDS] && successfullImport[Tab.REWARDS].length > 0;
  const areAchievements =
    successfullImport[Tab.ACHIEVEMENTS] &&
    successfullImport[Tab.ACHIEVEMENTS].length > 0;
  const areCampaigns =
    successfullImport[Tab.CAMPAIGNS] &&
    successfullImport[Tab.CAMPAIGNS].length > 0;
  const areReferrals =
    successfullImport[Tab.REFERRALS] &&
    successfullImport[Tab.REFERRALS].length > 0;
  const areAutomations =
    successfullImport[Tab.AUTOMATIONS] &&
    successfullImport[Tab.AUTOMATIONS].length > 0;

  const showCampaignSimulationInfo =
    areAutomations || areCampaigns || areReferrals;

  return (
    <>
      <Typography className={classes.importTitle}>
        {message(
          { id: "successfullyImportedToTargetTenant" },
          {
            tenant: originStore,
          },
        )}
      </Typography>
      {areWallets && (
        <ImportTableWrapper tableName={message({ id: "wallets" })}>
          <WalletSuccessfullImports
            wallets={successfullImport[Tab.WALLET] as BEWalletTypeFormat[]}
            disableTitle
          />
        </ImportTableWrapper>
      )}
      {areCustomEvents && (
        <ImportTableWrapper tableName={message({ id: "customEventSchemas" })}>
          <CustomSchemaSuccesfulImports
            disableTitle
            customEventSchemas={
              successfullImport[Tab.CUSTOM_EVENT] as ImportedCustomEventSchema[]
            }
          />
        </ImportTableWrapper>
      )}
      {areRewards && (
        <ImportTableWrapper tableName={message({ id: "rewards" })}>
          <RewardsSuccessfulImports
            disableTitle
            rewards={successfullImport[Tab.REWARDS] as RewardFieldsBE[]}
          />
        </ImportTableWrapper>
      )}
      {areAchievements && (
        <ImportTableWrapper tableName={message({ id: "achievements" })}>
          <AchievementsSuccessfulImports
            disableTitle
            achievements={
              successfullImport[Tab.ACHIEVEMENTS] as ImportedAchievement[]
            }
          />
        </ImportTableWrapper>
      )}
      {areCampaigns && (
        <ImportTableWrapper tableName={message({ id: "campaigns" })}>
          <CampaignsSuccessfulImports
            disableTitle
            campaigns={successfullImport[Tab.CAMPAIGNS] as CampaignBEData[]}
          />
        </ImportTableWrapper>
      )}
      {areReferrals && (
        <ImportTableWrapper tableName={message({ id: "referrals" })}>
          <ReferralsSuccessfullImports
            disableTitle
            referrals={successfullImport[Tab.REFERRALS] as CampaignBEData[]}
          />
        </ImportTableWrapper>
      )}
      {areAutomations && (
        <ImportTableWrapper tableName={message({ id: "automations" })}>
          <AutomationSuccessfulImports
            disableTitle
            campaigns={successfullImport[Tab.AUTOMATIONS] as CampaignBEData[]}
          />
        </ImportTableWrapper>
      )}
      {showCampaignSimulationInfo && (
        <Typography className={classes.simulateInfo}>
          {message({ id: "importCampaignsSimulateInfo" })}
        </Typography>
      )}
    </>
  );
};
